/**
 * 密码生成器：crypto.getRandomValues 密码学安全随机源，无第三方依赖。
 * 集成在 VaultItemForm 中生成后回填（明文仅在内存中传递）。
 */
import { useCallback, useState } from 'react';
import { Button, Field, Input, Toggle } from './ui';
import { Icon } from './icons';

const SETS = {
  lower: 'abcdefghijkmnopqrstuvwxyz',
  upper: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  digit: '23456789',
  symbol: '!@#$%^&*-_=+?',
};

interface GenOptions {
  length: number;
  upper: boolean;
  digit: boolean;
  symbol: boolean;
}

export function generatePassword(opts: GenOptions): string {
  const chars = SETS.lower + (opts.upper ? SETS.upper : '') + (opts.digit ? SETS.digit : '') + (opts.symbol ? SETS.symbol : '');
  const arr = new Uint32Array(opts.length);
  crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < opts.length; i += 1) out += chars[arr[i] % chars.length];
  return out;
}

interface PasswordGeneratorProps {
  onUse: (password: string) => void;
}

export function PasswordGenerator({ onUse }: PasswordGeneratorProps) {
  const [opts, setOpts] = useState<GenOptions>({ length: 20, upper: true, digit: true, symbol: true });
  const [preview, setPreview] = useState('');

  const roll = useCallback(() => setPreview(generatePassword(opts)), [opts]);

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <Icon name="key" className="h-4 w-4 text-indigo-500" />
        <span className="text-sm font-medium">密码生成器</span>
      </div>
      <Field label={`长度：${opts.length}`}>
        <input
          type="range"
          min={8}
          max={40}
          value={opts.length}
          onChange={(e) => setOpts((o) => ({ ...o, length: Number(e.target.value) }))}
          className="w-full accent-indigo-600"
        />
      </Field>
      <div className="flex flex-wrap gap-4">
        <Toggle checked={opts.upper} onChange={(v) => setOpts((o) => ({ ...o, upper: v }))} label="大写" />
        <Toggle checked={opts.digit} onChange={(v) => setOpts((o) => ({ ...o, digit: v }))} label="数字" />
        <Toggle checked={opts.symbol} onChange={(v) => setOpts((o) => ({ ...o, symbol: v }))} label="符号" />
      </div>
      <div className="flex gap-2">
        <Input readOnly value={preview} placeholder="点击生成" className="font-mono" />
        <Button size="sm" variant="secondary" onClick={roll}>
          <Icon name="refresh" className="h-4 w-4" />
        </Button>
      </div>
      <Button
        size="sm"
        disabled={!preview}
        onClick={() => {
          onUse(preview);
          setPreview('');
        }}
      >
        使用此密码
      </Button>
    </div>
  );
}
